import type {
  SelfDisclosureConfig,
  SelfDisclosureSender,
} from './config.schema';

/**
 * Art. 13 DSGVO two-layer block (Sanierungs-Spec §2.5) – one legal source of
 * truth, composed from the same building blocks as the rental-case privacy
 * policy.
 *
 * Layer cut:
 *  - Kurz-Schicht: max. 6 lines directly at the signature field.
 *  - Voll-Schicht: appendix page(s) of the same PDF.
 *
 * Legal basis is UNIFORMLY Art. 6 (1) (b) GDPR (Anbahnung) after the pivot
 * (OQ6): the form is by definition part of the contract initiation; the
 * former lit.-f branch existed only for the pure phase-A contact form, which
 * no longer exists as a product. The Widerspruchs-Hervorhebung tied to lit. f
 * is gone with it. Lawyer gate confirms the green-minimal contact set.
 *
 * Language layers: `language: 'de'` → one DE layer; `'en'` → one EN layer
 * (in-house translation, OQ3 – native review is a pre-launch gate);
 * `'de-en'` → DE layer first, EN layer second.
 *
 * WHITE-LABEL (Spec 2026-07-08 PR A): the downloadable self-disclosure PDF is a
 * white-label document – the landlord is the SOLE controller (Art. 13). It must
 * therefore never name a processor. The former Ametis-processor-clause render
 * switch was removed entirely (footgun): the Art.-13 full layer no longer
 * contains an "Empfänger / Auftragsverarbeiter" section. `AMETIS_PROCESSOR_CLAUSE`
 * still exists in `privacy-policy-text.ts` for the platform Consent-DSE – it is
 * NOT consumed here anymore.
 *
 * Streich-Liste (these sections NEVER appear in the layers authored here):
 * Art.-9 consent section, third-country transfer, processor/recipient section,
 * DPO note (only when `sender.dpoContact` is set), Art.-22 section, full
 * data-category list. `art13.test.ts` asserts the absence of the signal
 * strings ("Art. 9", "Einwilligung", "Drittland", the Ametis clause).
 */

export type Art13Language = 'de' | 'en';

export interface Art13LanguageLayer {
  language: Art13Language;
  /**
   * Überschrift über der Kurz-Schicht.
   *
   * 🔴 SIE FEHLTE — die Kurz-Schicht stand ohne jede Überschrift zwischen
   * Unterschriftenblock und Anhang, während die Voll-Schicht ihre eigene seit
   * jeher trägt (`buildFullLayer`, erste Zeile). Blatt Q7 zeichnet sie:
   *
   *   Q7: „Hinweise zum Datenschutz | Verantwortlich für die Verarbeitung:
   *   Michael Sandhoff, Kastanienallee 3, 10435 Berlin."
   *
   * ⚠️ EIGENES FELD, nicht `shortLines[0]`. Zwei Gründe: die Zusicherung
   * „höchstens sechs Zeilen" (§11.1.7) zählt weiter INHALT, nicht Inhalt plus
   * Überschrift; und `art13.test.ts` durchsucht `shortLines` nach
   * Rechts-Signalwörtern — eine Überschrift gehört dort nicht hinein.
   *
   * ⚠️ RECHTSTEXT: geändert wurde AUSSCHLIESSLICH die Überschrift. Kein Wort
   * der fünf Punkte darunter ist angefasst.
   */
  shortHeading: string;
  /** Short layer, ≤ 6 lines, rendered next to the signature field. */
  shortLines: string[];
  /** Full layer, rendered on the appendix page(s). */
  fullLines: string[];
}

/**
 * Die Überschrift der Kurz-Schicht je Sprache.
 *
 * Das Blatt ist deutsch; die englische Fassung folgt der Paarung, die die
 * Voll-Schicht schon trägt („Datenschutzinformationen nach Art. 13 DSGVO" /
 * „Privacy information pursuant to Art. 13 GDPR") — kurz, ohne Norm-Zitat.
 */
const SHORT_HEADING: Record<Art13Language, string> = {
  de: 'Hinweise zum Datenschutz',
  en: 'Privacy information',
};

const LEGAL_BASIS: Record<Art13Language, string> = {
  de: 'Art. 6 Abs. 1 lit. b DSGVO (Anbahnung des Mietverhältnisses)',
  en: 'Art. 6 (1) (b) GDPR (steps taken prior to entering into a tenancy agreement)',
};

function senderLine(
  sender: SelfDisclosureSender,
  language: Art13Language,
): string {
  const parts = [sender.displayName];

  if (sender.contactPerson) {
    parts.push(
      language === 'de'
        ? `Ansprechpartner: ${sender.contactPerson}`
        : `Contact person: ${sender.contactPerson}`,
    );
  }

  const address = [
    sender.street,
    [sender.postalCode, sender.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  if (address) parts.push(address);
  if (sender.email) parts.push(sender.email);

  return parts.join(', ');
}

/** Kurz-Schicht – 5 points, each on one line (≤ 6 lines, unit-tested). */
function buildShortLayer(
  config: SelfDisclosureConfig,
  language: Art13Language,
): string[] {
  if (language === 'de') {
    return [
      `Verantwortlicher: ${senderLine(config.sender, 'de')}`,
      `Zweck: Auswahl von Mietinteressenten und Anbahnung eines Mietverhältnisses. Rechtsgrundlage: ${LEGAL_BASIS.de}.`,
      'Speicherdauer: Löschung zeitnah nach der Vergabe-Entscheidung, spätestens nach 6 Monaten; bei Vertragsschluss Überführung in die Mietakte.',
      'Die Angaben erfolgen freiwillig; ohne sie kann die Bewerbung ggf. nicht berücksichtigt werden.',
      'Ausführliche Datenschutzinformationen finden Sie auf der letzten Seite dieses Formulars.',
    ];
  }

  return [
    `Controller: ${senderLine(config.sender, 'en')}`,
    `Purpose: selection of prospective tenants and initiation of a tenancy. Legal basis: ${LEGAL_BASIS.en}.`,
    'Retention: deletion promptly after the letting decision, at the latest after 6 months; if a tenancy agreement is concluded, transfer to the tenancy file.',
    'Providing the information is voluntary; without it, the application may not be considered.',
    'You will find the full privacy information on the last page of this form.',
  ];
}

/** Voll-Schicht – appendix page(s). */
function buildFullLayer(
  config: SelfDisclosureConfig,
  language: Art13Language,
): string[] {
  const lines: string[] = [];

  if (language === 'de') {
    lines.push('Datenschutzinformationen nach Art. 13 DSGVO');
    lines.push('');
    lines.push(`Verantwortlicher: ${senderLine(config.sender, 'de')}`);

    // DPO note only when explicitly provided (Streich-Liste otherwise).
    if (config.sender.dpoContact) {
      lines.push(`Datenschutzbeauftragter: ${config.sender.dpoContact}`);
    }

    lines.push('');
    lines.push(
      `Zweck und Rechtsgrundlage: Auswahl von Mietinteressenten und Anbahnung eines Mietverhältnisses auf Grundlage von ${LEGAL_BASIS.de}.`,
    );
    lines.push(
      'Speicherdauer: Ihre Angaben werden zeitnah nach der Vergabe-Entscheidung gelöscht, spätestens nach 6 Monaten; bei Vertragsschluss werden sie in die Mietakte überführt.',
    );
    lines.push(
      'Erhoben werden ausschließlich die in diesem Formular abgefragten Angaben.',
    );
    lines.push('');
    lines.push(
      'Ihre Rechte: Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch (Art. 15 bis 21 DSGVO).',
    );
    lines.push(
      'Sie haben zudem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren.',
    );

    lines.push('');
    lines.push(
      'Keine unaufgeforderte Zusendung nicht erforderlicher Unterlagen!',
    );

    return lines;
  }

  lines.push('Privacy information pursuant to Art. 13 GDPR');
  lines.push('');
  lines.push(`Controller: ${senderLine(config.sender, 'en')}`);

  if (config.sender.dpoContact) {
    lines.push(`Data protection officer: ${config.sender.dpoContact}`);
  }

  lines.push('');
  lines.push(
    `Purpose and legal basis: selection of prospective tenants and initiation of a tenancy on the basis of ${LEGAL_BASIS.en}.`,
  );
  lines.push(
    'Retention: your details are deleted promptly after the letting decision, at the latest after 6 months; if a tenancy agreement is concluded, they are transferred to the tenancy file.',
  );
  lines.push('Only the details requested in this form are collected.');
  lines.push('');
  lines.push(
    'Your rights: you have the right of access, rectification, erasure, restriction of processing and data portability, as well as the right to object (Art. 15 to 21 GDPR).',
  );
  lines.push(
    'You also have the right to lodge a complaint with a data protection supervisory authority.',
  );

  lines.push('');
  lines.push(
    'Do not send documents that were not requested and are not required!',
  );

  return lines;
}

function layerLanguages(config: SelfDisclosureConfig): Art13Language[] {
  switch (config.language) {
    case 'de':
      return ['de'];
    case 'en':
      return ['en'];
    case 'de-en':
      return ['de', 'en'];
  }
}

/**
 * Builds the Art.-13 layers per config language: one layer for `de`/`en`,
 * two layers (DE first, EN second) for `de-en`.
 */
export function buildArt13Layers(
  config: SelfDisclosureConfig,
): Art13LanguageLayer[] {
  return layerLanguages(config).map((language) => ({
    language,
    shortHeading: SHORT_HEADING[language],
    shortLines: buildShortLayer(config, language),
    fullLines: buildFullLayer(config, language),
  }));
}
